/**
 * Control Portal - Admin Dashboard
 * Global monitoring for SentinelX admins
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Building2, Wifi, WifiOff, Truck, Thermometer, Clock, 
  AlertTriangle, Bell, RefreshCw, ChevronRight, Users,
  Activity, TrendingUp, Shield, Loader2
} from 'lucide-react';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function ControlDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [deviceLocations, setDeviceLocations] = useState([]);
  const [customerSummary, setCustomerSummary] = useState([]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, alertsRes, licensesRes, locationsRes, customersRes] = await Promise.all([
        controlApi.getDashboardStats(),
        controlApi.getRecentAlerts({ limit: 5 }),
        controlApi.getExpiringLicenses({ days: 7, limit: 5 }),
        controlApi.getDeviceLocations({ limit: 500 }),
        controlApi.getCustomerSummary()
      ]);
      
      setStats(statsRes.data);
      setRecentAlerts(alertsRes.data);
      setExpiringLicenses(licensesRes.data);
      setDeviceLocations(locationsRes.data);
      setCustomerSummary(customersRes.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const kpis = [
    { label: 'Active Customers', value: stats.active_customers || 0, icon: Building2, color: 'blue', path: '/control/tenants' },
    { label: 'Total Devices', value: stats.total_devices || 0, icon: Wifi, color: 'slate', path: '/control/devices' },
    { label: 'Devices Online', value: stats.online_devices || 0, icon: Activity, color: 'green', path: '/control/devices?status=online' },
    { label: 'Devices Offline', value: stats.offline_devices || 0, icon: WifiOff, color: stats.offline_devices > 0 ? 'red' : 'slate', path: '/control/devices?status=offline' },
    { label: 'Trips Running', value: stats.trips_running || 0, icon: Truck, color: 'teal', path: '/control/work-orders' },
    { label: 'Temp Breaches (24h)', value: stats.temp_breaches_24h || 0, icon: Thermometer, color: stats.temp_breaches_24h > 0 ? 'red' : 'green', path: '/control/alerts?type=temp_breach' },
    { label: 'Expiring Licenses', value: stats.expiring_licenses || 0, icon: Clock, color: stats.expiring_licenses > 0 ? 'amber' : 'slate', path: '/control/licenses' },
    { label: 'Open Alerts', value: stats.open_alerts || 0, icon: AlertTriangle, color: stats.open_alerts > 0 ? 'red' : 'green', path: '/control/alerts' },
  ];

  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
    teal: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    slate: 'bg-slate-700/50 text-slate-400 border-slate-600/30',
  };

  if (loading && !stats.timestamp) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="dashboard-title">
            <Shield className="w-7 h-7 text-blue-500" />
            SentinelX Control Center
          </h1>
          <p className="text-slate-400 mt-1">Global monitoring and administration</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchDashboardData}
          disabled={loading}
          className="border-slate-700 text-slate-300"
          data-testid="refresh-dashboard"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <button
              key={i}
              onClick={() => navigate(kpi.path)}
              className="bg-slate-900 rounded-xl p-4 border border-slate-800 hover:border-slate-700 transition-all text-left group"
              data-testid={`kpi-${kpi.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${colorClasses[kpi.color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors" />
              </div>
              <p className="text-3xl font-bold text-white">{kpi.value}</p>
              <p className="text-slate-400 text-sm mt-1">{kpi.label}</p>
            </button>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live Map */}
        <div className="lg:col-span-2 bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Live Device Map</h2>
            <Badge variant="outline" className="border-green-500 text-green-400">
              {deviceLocations.filter(d => d.is_online).length} online
            </Badge>
          </div>
          <div className="h-80" data-testid="live-map">
            <MapContainer
              center={[20.5937, 78.9629]}
              zoom={5}
              style={{ height: '100%', width: '100%' }}
              className="z-0"
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap'
              />
              {deviceLocations.map((device, i) => (
                <CircleMarker
                  key={i}
                  center={[device.latitude, device.longitude]}
                  radius={6}
                  fillColor={device.is_online ? '#22c55e' : '#ef4444'}
                  color={device.is_online ? '#16a34a' : '#dc2626'}
                  weight={2}
                  opacity={0.8}
                  fillOpacity={0.6}
                >
                  <Popup>
                    <div className="text-sm min-w-[150px]">
                      <p className="font-bold text-slate-900">{device.device_uid}</p>
                      <p className="text-slate-600">{device.tenant_name}</p>
                      <div className="mt-2 space-y-1">
                        <p>Temp: {device.temperature ? `${device.temperature}°C` : 'N/A'}</p>
                        <p>Battery: {device.battery_level || 'N/A'}%</p>
                        <p className={device.is_online ? 'text-green-600' : 'text-red-600'}>
                          {device.is_online ? 'Online' : 'Offline'}
                        </p>
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="bg-slate-900 rounded-xl border border-slate-800">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-400" />
              Recent Alerts
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/control/alerts')}
              className="text-blue-400 hover:text-blue-300"
            >
              View All
            </Button>
          </div>
          <div className="p-3 max-h-72 overflow-y-auto" data-testid="recent-alerts">
            {recentAlerts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No recent alerts</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 cursor-pointer"
                    onClick={() => navigate(`/control/alerts/${alert.id}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        alert.level === 'critical' ? 'bg-red-500/20' : 'bg-amber-500/20'
                      }`}>
                        <AlertTriangle className={`w-4 h-4 ${
                          alert.level === 'critical' ? 'text-red-400' : 'text-amber-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{alert.message}</p>
                        <p className="text-slate-500 text-xs mt-1">
                          {alert.tenant_name} - {alert.device_uid || 'System'}
                        </p>
                        <p className="text-slate-600 text-xs">
                          {new Date(alert.triggered_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Summary */}
        <div className="bg-slate-900 rounded-xl border border-slate-800">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              Customer Summary
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/control/tenants')}
              className="text-blue-400 hover:text-blue-300"
            >
              Manage
            </Button>
          </div>
          <div className="overflow-x-auto" data-testid="customer-summary">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left p-3 text-slate-400 font-medium text-sm">Customer</th>
                  <th className="text-center p-3 text-slate-400 font-medium text-sm">Devices</th>
                  <th className="text-center p-3 text-slate-400 font-medium text-sm">Users</th>
                  <th className="text-center p-3 text-slate-400 font-medium text-sm">Status</th>
                </tr>
              </thead>
              <tbody>
                {customerSummary.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-slate-500">
                      No customers found
                    </td>
                  </tr>
                ) : (
                  customerSummary.slice(0, 5).map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-b border-slate-800/50 hover:bg-slate-800/50 cursor-pointer"
                    onClick={() => navigate(`/control/tenants/${customer.id}`)}
                  >
                    <td className="p-3">
                      <p className="text-white font-medium">{customer.name}</p>
                      <p className="text-slate-500 text-xs">{customer.code}</p>
                    </td>
                    <td className="p-3 text-center text-white">{customer.device_count}</td>
                    <td className="p-3 text-center text-white">{customer.user_count}</td>
                    <td className="p-3 text-center">
                      <Badge variant={customer.status === 'active' ? 'default' : 'secondary'}>
                        {customer.status}
                      </Badge>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Expiring Licenses */}
        <div className="bg-slate-900 rounded-xl border border-slate-800">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Expiring Licenses (7 days)
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/control/licenses')}
              className="text-blue-400 hover:text-blue-300"
            >
              View All
            </Button>
          </div>
          <div className="overflow-x-auto" data-testid="expiring-licenses">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left p-3 text-slate-400 font-medium text-sm">Device</th>
                  <th className="text-left p-3 text-slate-400 font-medium text-sm">Customer</th>
                  <th className="text-left p-3 text-slate-400 font-medium text-sm">Plan</th>
                  <th className="text-center p-3 text-slate-400 font-medium text-sm">Days Left</th>
                </tr>
              </thead>
              <tbody>
                {expiringLicenses.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-slate-500">
                      No expiring licenses
                    </td>
                  </tr>
                ) : (
                  expiringLicenses.map((license) => (
                  <tr
                    key={license.id}
                    className="border-b border-slate-800/50 hover:bg-slate-800/50 cursor-pointer"
                    onClick={() => navigate(`/control/devices/${license.device_id}`)}
                  >
                    <td className="p-3">
                      <p className="text-white font-mono text-sm">{license.device_uid}</p>
                    </td>
                    <td className="p-3 text-slate-300 text-sm">{license.tenant_name}</td>
                    <td className="p-3">
                      <Badge variant="outline" className="border-slate-600">
                        {license.plan_code}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`font-bold ${
                        license.days_left <= 3 ? 'text-red-400' : 'text-amber-400'
                      }`}>
                        {license.days_left}d
                      </span>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
